import { useState, useEffect } from 'react';
import { Newspaper } from 'lucide-react';

/**
 * CoverImage — resilient <img> wrapper.
 *
 * Renders the given `src` inside an absolute-filled layer. When the image
 * fails to load (404/expired/CORS/broken URL) OR when `src` is falsy, it
 * swaps to a branded placeholder that keeps the exact same box, so the
 * card layout (title, badge, category, etc.) never shifts.
 *
 * The parent MUST provide the aspect ratio / height (e.g. `aspect-video`).
 * This component itself always fills the parent (absolute inset-0).
 */
export default function CoverImage({
  src,
  alt = '',
  className = '',
  Icon = Newspaper,
  loading = 'lazy',
  decoding = 'async',
  testId,
}) {
  const [failed, setFailed] = useState(false);

  // Reset error state whenever the src changes so a fresh URL gets a try.
  useEffect(() => {
    setFailed(false);
  }, [src]);

  const showFallback = !src || failed;

  return (
    <div
      className="absolute inset-0 w-full h-full bg-surface"
      data-testid={testId}
      data-cover-state={showFallback ? 'fallback' : 'image'}
    >
      {showFallback ? (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-ruby/10 via-surface to-black">
          <Icon className="w-10 h-10 text-ruby/40" aria-hidden="true" />
          <span className="sr-only">{alt || 'Imagem indisponível'}</span>
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          loading={loading}
          decoding={decoding}
          onError={() => setFailed(true)}
          className={
            'w-full h-full object-cover ' + className
          }
        />
      )}
    </div>
  );
}
